import { useEffect, useState } from "react";
import { Plus, PencilSimple, Trash, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useCompany } from "../../context/CompanyContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const empty = { name: "", gstin: "", address: "", base_currency: "INR", overhead_percent: 10 };

export default function Companies() {
  const { companies, refresh, setActive, activeId } = useCompany();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  useEffect(() => { refresh(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    const payload = { ...form, overhead_percent: parseFloat(form.overhead_percent || 0) };
    try {
      if (editing) {
        await api.put(`/finance/companies/${editing.id}`, payload);
        toast.success("Company updated");
      } else {
        const { data } = await api.post("/finance/companies", payload);
        setActive(data.id);
        toast.success("Company created");
      }
      setOpen(false);
      await refresh();
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      if (status === 403) {
        toast.error("Access denied — you need the 'finance' role. Ask the administrator to grant it.");
      } else if (status === 401) {
        toast.error("Session expired — please sign in again.");
      } else {
        toast.error(`Failed to save: ${detail}`);
      }
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this company? Contacts will also be removed.")) return;
    try {
      await api.delete(`/finance/companies/${id}`);
      toast.success("Deleted");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <Layout
      title="Companies"
      subtitle="Reyansh manages financials across all your registered companies"
      actions={
        <button onClick={openNew} data-testid="add-company-button" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] transition-all active:scale-95">
          <Plus size={16} weight="bold" /> Add Company
        </button>
      }
    >
      {companies.length === 0 ? (
        <div className="border border-black/10 p-16 text-center" data-testid="empty-companies">
          <Buildings size={36} className="mx-auto text-[#D90429]" />
          <h3 className="font-display text-2xl font-bold tracking-tight mt-4">No companies yet</h3>
          <p className="mt-2 text-[#717680]">Add your first company to start tracking work orders, payments and expenses.</p>
          <button onClick={openNew} data-testid="empty-add-company" className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">
            <Plus size={16} weight="bold" /> Add Company
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="companies-grid">
          {companies.map((c) => (
            <div key={c.id} className={`border bg-white p-5 hover:-translate-y-0.5 hover:shadow-sm transition-all ${activeId === c.id ? "border-[#D90429]" : "border-black/10"}`} data-testid={`company-card-${c.id}`}>
              <div className="flex items-start justify-between">
                <Buildings size={22} weight="bold" className="text-[#D90429]" />
                <div className="flex gap-1">
                  {activeId !== c.id && (
                    <button onClick={() => setActive(c.id)} className="text-[10px] font-mono-shelv uppercase tracking-widest text-[#D90429] hover:underline px-2" data-testid={`activate-company-${c.id}`}>Set Active</button>
                  )}
                  {activeId === c.id && <span className="text-[10px] font-mono-shelv uppercase tracking-widest bg-[#D90429] text-white px-2 py-0.5">Active</span>}
                  <button onClick={() => openEdit(c)} className="p-1.5 hover:bg-black/5" data-testid={`edit-company-${c.id}`}><PencilSimple size={14} /></button>
                  <button onClick={() => remove(c.id)} className="p-1.5 hover:bg-[#D90429]/10 text-[#D90429]" data-testid={`delete-company-${c.id}`}><Trash size={14} /></button>
                </div>
              </div>
              <h3 className="font-display text-2xl font-bold tracking-tight mt-3">{c.name}</h3>
              {c.gstin && <div className="text-xs font-mono-shelv text-[#717680] mt-1">GSTIN {c.gstin}</div>}
              {c.address && <div className="text-sm text-[#4A4D57] mt-2">{c.address}</div>}
              <div className="mt-4 pt-4 border-t border-black/5 flex justify-between text-xs">
                <div><div className="text-[#717680]">Base currency</div><div className="font-semibold">{c.base_currency}</div></div>
                <div className="text-right"><div className="text-[#717680]">Overhead</div><div className="font-semibold">{c.overhead_percent}%</div></div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg">
          <DialogHeader><DialogTitle className="font-display tracking-tight">{editing ? "Edit company" : "New company"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Company Name *</Label><Input data-testid="company-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none" /></div>
            <div><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} className="rounded-none" /></div>
            <div>
              <Label>Base Currency</Label>
              <Select value={form.base_currency} onValueChange={(v) => setForm({ ...form, base_currency: v })}>
                <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="INR">INR ₹</SelectItem><SelectItem value="USD">USD $</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Address</Label><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-none" /></div>
            <div className="col-span-2">
              <Label>Overhead % (applied for net profit calc)</Label>
              <Input type="number" value={form.overhead_percent} onChange={(e) => setForm({ ...form, overhead_percent: e.target.value })} className="rounded-none" />
              <p className="text-xs text-[#717680] mt-1">Net profit = Gross profit − (Work Order value × Overhead%)</p>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={save} data-testid="save-company-button" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">{editing ? "Save" : "Add"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
